import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import nl from '../i18n/nl.js';

const STORAGE_KEY = 'hw_language';
const SUPPORTED = ['en', 'nl'];

export const LanguageContext = createContext({
  language: 'en',
  setLanguage: () => {},
  t: (key) => key,
  locale: 'en-US',
});

function interpolate(str, vars) {
  if (!vars) return str;
  return String(str).replace(/\{(\w+)\}/g, (_, k) => (
    vars[k] === undefined || vars[k] === null ? `{${k}}` : String(vars[k])
  ));
}

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved && SUPPORTED.includes(saved)) return saved;
    } catch (e) {
      /* ignore */
    }
    return 'en';
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, language);
    } catch (e) {
      /* ignore */
    }
    if (typeof document !== 'undefined') {
      document.documentElement.lang = language === 'nl' ? 'nl' : 'en';
    }
  }, [language]);

  const setLanguage = useCallback((lang) => {
    if (SUPPORTED.includes(lang)) {
      setLanguageState(lang);
    }
  }, []);

  const t = useCallback((key, vars) => {
    if (key === undefined || key === null) return '';
    const source = String(key);
    const translated = language === 'nl' ? (nl[source] ?? source) : source;
    return interpolate(translated, vars);
  }, [language]);

  const locale = language === 'nl' ? 'nl-NL' : 'en-US';

  const value = useMemo(
    () => ({ language, setLanguage, t, locale }),
    [language, setLanguage, t, locale]
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);

export default LanguageContext;
